/**
 * @fileoverview MonthComparisonReport - Component for comparing two time periods.
 *
 * @description This component provides:
 * - Period selection (single month or date range comparison)
 * - Overall financial summary with difference and % change
 * - Transaction type breakdown with category details
 * - Category comparison table
 * - Transaction list toggle between periods
 */

/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable complexity */

import { useState, useEffect } from 'react';
import { transactionAPI } from '../../services/api';
import { formatCurrency as formatMoney } from '../../utils/moneyUtils';
import ToggleSwitch from '../shared/ToggleSwitch';
import styles from './MonthComparisonReport.module.css';

/**
 * Helper function to calculate month count between two dates
 */
const getMonthCount = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    return months;
};

/**
 * Helper function to format month range for display
 * Parses YYYY-MM-DD strings manually to avoid timezone issues
 */
const formatPeriodDisplay = (startDate, endDate) => {
    // Parse YYYY-MM-DD to avoid timezone issues
    const parseDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    const formatMonth = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
        return formatMonth(start);
    }

    return `${formatMonth(start)} - ${formatMonth(end)}`;
};

/**
 * Month Comparison Report Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.budgetInfo - Budget metadata including start month
 */
function MonthComparisonReport({ budgetInfo }) {
    const [isDateRange, setIsDateRange] = useState(false);

    // Period 1 selection
    const [period1Month, setPeriod1Month] = useState('');
    const [period1Start, setPeriod1Start] = useState('');
    const [period1End, setPeriod1End] = useState('');

    // Period 2 selection
    const [period2Month, setPeriod2Month] = useState('');
    const [period2Start, setPeriod2Start] = useState('');
    const [period2End, setPeriod2End] = useState('');

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    const [availableMonths, setAvailableMonths] = useState([]);

    // Fetch available months from tblmonthbalances
    useEffect(() => {
        const fetchAvailableMonths = async () => {
            try {
                const months = await transactionAPI.getAvailableMonths();
                setAvailableMonths(months);
            } catch (err) {
                // Failed to fetch months - use empty array
                setAvailableMonths([]);
            }
        };

        fetchAvailableMonths();
    }, [budgetInfo?.budgetKey]);

    // Reset report state when budget changes
    useEffect(() => {
        setReportData(null);
        setHasSearched(false);
        setError(null);
    }, [budgetInfo?.budgetKey]);

    const handleSearch = async () => {
        let p1Start, p1End, p2Start, p2End;

        if (isDateRange) {
            if (!period1Start || !period1End || !period2Start || !period2End) {
                setError('Please select all date range fields');
                return;
            }
            p1Start = `${period1Start}-01`;
            p1End = `${period1End}-01`;
            p2Start = `${period2Start}-01`;
            p2End = `${period2End}-01`;
        } else {
            if (!period1Month || !period2Month) {
                setError('Please select both periods');
                return;
            }
            p1Start = p1End = `${period1Month}-01`;
            p2Start = p2End = `${period2Month}-01`;
        }

        // Validation: periods not identical
        if (p1Start === p2Start && p1End === p2End) {
            setError('Please select different time periods to compare');
            return;
        }

        // Validation: start before end for each period
        if (new Date(p1Start) > new Date(p1End)) {
            setError('Period 1: Start month must be before or equal to end month');
            return;
        }
        if (new Date(p2Start) > new Date(p2End)) {
            setError('Period 2: Start month must be before or equal to end month');
            return;
        }

        // Validation: max 12 months per period
        const period1Months = getMonthCount(p1Start, p1End);
        const period2Months = getMonthCount(p2Start, p2End);

        if (period1Months > 12) {
            setError('Period 1 cannot exceed 12 months');
            return;
        }
        if (period2Months > 12) {
            setError('Period 2 cannot exceed 12 months');
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            // Send first day of month for fdtmbudgetmonth filtering
            // Backend uses fdtmbudgetmonth which is always the first of the month
            const data = await transactionAPI.getMonthComparisonReport(
                p1Start,
                p1End,
                p2Start,
                p2End
            );

            // Store period info with data
            // Note: Backend also returns period1/period2, but we override with our calculated values
            setReportData(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch comparison data');
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setPeriod1Month('');
        setPeriod1Start('');
        setPeriod1End('');
        setPeriod2Month('');
        setPeriod2Start('');
        setPeriod2End('');
        setReportData(null);
        setHasSearched(false);
        setError(null);
    };

    return (
        <div className={styles.container}>
            {/* Period Selection */}
            <div className={styles.filtersSection}>
                <h2 className={styles.sectionTitle}>Search Filters</h2>

                <div className={styles.toggleRow}>
                    <ToggleSwitch
                        checked={isDateRange}
                        onChange={(checked) => {
                            setIsDateRange(checked);
                            handleClear();
                        }}
                        label="Compare date ranges"
                    />
                </div>

                {!isDateRange ? (
                    <div className={styles.filterRow}>
                        <div className={styles.filterGroup}>
                            <label className={styles.label}>Period 1</label>
                            <select
                                value={period1Month}
                                onChange={(e) => setPeriod1Month(e.target.value)}
                                className={styles.select}
                            >
                                <option value="">Select month...</option>
                                {availableMonths.map(month => {
                                    // Parse YYYY-MM to avoid timezone issues
                                    const [year, monthNum] = month.split('-');
                                    const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                                    const displayText = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                                    return (
                                        <option key={month} value={month}>
                                            {displayText}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className={styles.filterGroup}>
                            <label className={styles.label}>Period 2</label>
                            <select
                                value={period2Month}
                                onChange={(e) => setPeriod2Month(e.target.value)}
                                className={styles.select}
                            >
                                <option value="">Select month...</option>
                                {availableMonths.map(month => {
                                    // Parse YYYY-MM to avoid timezone issues
                                    const [year, monthNum] = month.split('-');
                                    const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                                    const displayText = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                                    return (
                                        <option key={month} value={month}>
                                            {displayText}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                ) : (
                    <div className={styles.dateRangeContainer}>
                        <div className={styles.periodGroup}>
                            <h3 className={styles.periodLabel}>Period 1</h3>
                            <div className={styles.dateRangeRow}>
                                <div className={styles.filterGroup}>
                                    <label className={styles.label}>From</label>
                                    <select
                                        value={period1Start}
                                        onChange={(e) => setPeriod1Start(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">Select month...</option>
                                        {availableMonths.map(month => (
                                            <option key={month} value={month}>
                                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.filterGroup}>
                                    <label className={styles.label}>To</label>
                                    <select
                                        value={period1End}
                                        onChange={(e) => setPeriod1End(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">Select month...</option>
                                        {availableMonths.map(month => (
                                            <option key={month} value={month}>
                                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className={styles.periodGroup}>
                            <h3 className={styles.periodLabel}>Period 2</h3>
                            <div className={styles.dateRangeRow}>
                                <div className={styles.filterGroup}>
                                    <label className={styles.label}>From</label>
                                    <select
                                        value={period2Start}
                                        onChange={(e) => setPeriod2Start(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">Select month...</option>
                                        {availableMonths.map(month => (
                                            <option key={month} value={month}>
                                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.filterGroup}>
                                    <label className={styles.label}>To</label>
                                    <select
                                        value={period2End}
                                        onChange={(e) => setPeriod2End(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">Select month...</option>
                                        {availableMonths.map(month => (
                                            <option key={month} value={month}>
                                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.buttonRow}>
                    <button onClick={handleSearch} className={styles.searchButton} disabled={loading}>
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                    {hasSearched && (
                        <button onClick={handleClear} className={styles.clearButton}>
                            Clear Results
                        </button>
                    )}
                </div>

                {error && (
                    <div className={styles.errorMessage}>{error}</div>
                )}
            </div>

            {/* Results */}
            {hasSearched && reportData && !loading && (
                <ComparisonResults
                    reportData={reportData}
                />
            )}

            {hasSearched && !reportData && !loading && (
                <div className={styles.noResultsMessage}>
                    No comparison data available for the selected periods.
                </div>
            )}
        </div>
    );
}

/**
 * Comparison Results Component
 */
function ComparisonResults({ reportData }) {
    const { period1, period2, categories, transactionTypes, categoryByType } = reportData;

    // Calculate overall totals
    const period1Total = categories.reduce((sum, cat) => sum + (cat.period1Balance || 0), 0);
    const period2Total = categories.reduce((sum, cat) => sum + (cat.period2Balance || 0), 0);
    const period1Credits = categories.reduce((sum, cat) => sum + (cat.period1CreditsIn || 0), 0);
    const period2Credits = categories.reduce((sum, cat) => sum + (cat.period2CreditsIn || 0), 0);
    const period1Debits = categories.reduce((sum, cat) => sum + (cat.period1DebitsOut || 0), 0);
    const period2Debits = categories.reduce((sum, cat) => sum + (cat.period2DebitsOut || 0), 0);

    const totalDiff = period2Total - period1Total;
    const creditsDiff = period2Credits - period1Credits;
    const debitsDiff = period2Debits - period1Debits;

    const totalPercent = period1Total !== 0 ? ((totalDiff / Math.abs(period1Total)) * 100) : 0;
    const creditsPercent = period1Credits !== 0 ? ((creditsDiff / period1Credits) * 100) : 0;
    const debitsPercent = period1Debits !== 0 ? ((debitsDiff / period1Debits) * 100) : 0;

    const monthsMatch = period1.months === period2.months;

    return (
        <>
            {/* Period Overview */}
            <div className={styles.summarySection}>
                <h2 className={styles.sectionTitle}>Period Overview</h2>
                <div className={styles.periodOverview}>
                    <div className={styles.periodCard}>
                        <div className={styles.periodTitle}>Period 1</div>
                        <div className={styles.periodDate}>{formatPeriodDisplay(period1.start, period1.end)}</div>
                        <div className={styles.periodMonths}>({period1.months} month{period1.months !== 1 ? 's' : ''})</div>
                    </div>
                    <div className={styles.periodCard}>
                        <div className={styles.periodTitle}>Period 2</div>
                        <div className={styles.periodDate}>{formatPeriodDisplay(period2.start, period2.end)}</div>
                        <div className={styles.periodMonths}>({period2.months} month{period2.months !== 1 ? 's' : ''})</div>
                    </div>
                </div>
                {!monthsMatch && (
                    <div className={styles.monthsDifferentNote}>
                        Note: Periods have different durations ({period1.months} vs {period2.months} months)
                    </div>
                )}
            </div>

            {/* Overall Financial Summary */}
            <div className={styles.summarySection}>
                <h2 className={styles.sectionTitle}>Overall Financial Summary</h2>
                <OverallSummaryTable
                    period1Total={period1Total}
                    period2Total={period2Total}
                    totalDiff={totalDiff}
                    totalPercent={totalPercent}
                    period1Credits={period1Credits}
                    period2Credits={period2Credits}
                    creditsDiff={creditsDiff}
                    creditsPercent={creditsPercent}
                    period1Debits={period1Debits}
                    period2Debits={period2Debits}
                    debitsDiff={debitsDiff}
                    debitsPercent={debitsPercent}
                    period1Months={period1.months}
                    period2Months={period2.months}
                />
            </div>

            {/* Transaction Type Breakdown */}
            <div className={styles.summarySection}>
                <h2 className={styles.sectionTitle}>Transaction Type Breakdown</h2>
                <TransactionTypeBreakdown
                    transactionTypes={transactionTypes}
                    categoryByType={categoryByType}
                />
            </div>

            {/* Category Comparison */}
            <div className={styles.summarySection}>
                <h2 className={styles.sectionTitle}>Category Comparison</h2>
                <CategoryComparison
                    categories={categories}
                />
            </div>
        </>
    );
}

/**
 * Overall Summary Table Component
 */
function OverallSummaryTable({
    period1Total, period2Total, totalDiff, totalPercent,
    period1Credits, period2Credits, creditsDiff, creditsPercent,
    period1Debits, period2Debits, debitsDiff, debitsPercent,
    period1Months, period2Months
}) {
    const formatCurrency = (value) => formatMoney(Math.abs(value));
    const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

    const getTrendArrow = (diff) => {
        if (diff > 0) return '↑';
        if (diff < 0) return '↓';
        return '→';
    };

    return (
        <>
            {/* Desktop Table View */}
            <div className={styles.overallSummaryTable}>
                <div className={styles.tableHeader}>
                    <span className={styles.headerCell}>Metric</span>
                    <span className={styles.headerCell}>Period 1</span>
                    <span className={styles.headerCell}>Period 2</span>
                    <span className={styles.headerCell}>Difference</span>
                    <span className={styles.headerCell}>% Change</span>
                    <span className={styles.headerCell}>Avg/Month P1</span>
                    <span className={styles.headerCell}>Avg/Month P2</span>
                </div>

                {/* Net Total */}
                <div className={styles.tableRow}>
                    <span className={styles.cell}>Net Total</span>
                    <span className={styles.cell}>{formatCurrency(period1Total)}</span>
                    <span className={styles.cell}>{formatCurrency(period2Total)}</span>
                    <span className={`${styles.cell} ${totalDiff >= 0 ? styles.positive : styles.negative}`}>
                        {getTrendArrow(totalDiff)} {formatCurrency(totalDiff)}
                    </span>
                    <span className={`${styles.cell} ${totalPercent >= 0 ? styles.positive : styles.negative}`}>
                        {formatPercent(totalPercent)}
                    </span>
                    <span className={styles.cell}>{formatCurrency(period1Total / period1Months)}</span>
                    <span className={styles.cell}>{formatCurrency(period2Total / period2Months)}</span>
                </div>

                {/* Credits */}
                <div className={styles.tableRow}>
                    <span className={styles.cell}>Total In (Credits)</span>
                    <span className={`${styles.cell} ${styles.creditCell}`}>{formatCurrency(period1Credits)}</span>
                    <span className={`${styles.cell} ${styles.creditCell}`}>{formatCurrency(period2Credits)}</span>
                    <span className={`${styles.cell} ${creditsDiff >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                        {getTrendArrow(creditsDiff)} {formatCurrency(creditsDiff)}
                    </span>
                    <span className={`${styles.cell} ${creditsPercent >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                        {formatPercent(creditsPercent)}
                    </span>
                    <span className={styles.cell}>{formatCurrency(period1Credits / period1Months)}</span>
                    <span className={styles.cell}>{formatCurrency(period2Credits / period2Months)}</span>
                </div>

                {/* Debits */}
                <div className={styles.tableRow}>
                    <span className={styles.cell}>Total Out (Debits)</span>
                    <span className={`${styles.cell} ${styles.debitCell}`}>{formatCurrency(period1Debits)}</span>
                    <span className={`${styles.cell} ${styles.debitCell}`}>{formatCurrency(period2Debits)}</span>
                    <span className={`${styles.cell} ${debitsDiff <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                        {getTrendArrow(debitsDiff)} {formatCurrency(debitsDiff)}
                    </span>
                    <span className={`${styles.cell} ${debitsPercent <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                        {formatPercent(debitsPercent)}
                    </span>
                    <span className={styles.cell}>{formatCurrency(period1Debits / period1Months)}</span>
                    <span className={styles.cell}>{formatCurrency(period2Debits / period2Months)}</span>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className={styles.overallSummaryMobile}>
                {/* Net Total Card */}
                <div className={styles.summaryCard}>
                    <div className={styles.summaryCardHeader}>
                        <span className={styles.summaryMetricName}>Net Total</span>
                    </div>
                    <div className={styles.summaryCardBody}>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 1</div>
                            <div className={styles.summaryAmount}>{formatCurrency(period1Total)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period1Total / period1Months)}</div>
                        </div>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 2</div>
                            <div className={styles.summaryAmount}>{formatCurrency(period2Total)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period2Total / period2Months)}</div>
                        </div>
                        <div className={styles.summaryChangeSection}>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>Difference:</span>
                                <span className={`${styles.summaryChangeValue} ${totalDiff >= 0 ? styles.positive : styles.negative}`}>
                                    {getTrendArrow(totalDiff)} {formatCurrency(totalDiff)}
                                </span>
                            </div>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>% Change:</span>
                                <span className={`${styles.summaryChangeValue} ${totalPercent >= 0 ? styles.positive : styles.negative}`}>
                                    {formatPercent(totalPercent)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Credits Card */}
                <div className={styles.summaryCard}>
                    <div className={styles.summaryCardHeader}>
                        <span className={styles.summaryMetricName}>Total In (Credits)</span>
                    </div>
                    <div className={styles.summaryCardBody}>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 1</div>
                            <div className={`${styles.summaryAmount} ${styles.creditCell}`}>{formatCurrency(period1Credits)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period1Credits / period1Months)}</div>
                        </div>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 2</div>
                            <div className={`${styles.summaryAmount} ${styles.creditCell}`}>{formatCurrency(period2Credits)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period2Credits / period2Months)}</div>
                        </div>
                        <div className={styles.summaryChangeSection}>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>Difference:</span>
                                <span className={`${styles.summaryChangeValue} ${creditsDiff >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                                    {getTrendArrow(creditsDiff)} {formatCurrency(creditsDiff)}
                                </span>
                            </div>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>% Change:</span>
                                <span className={`${styles.summaryChangeValue} ${creditsPercent >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                                    {formatPercent(creditsPercent)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Debits Card */}
                <div className={styles.summaryCard}>
                    <div className={styles.summaryCardHeader}>
                        <span className={styles.summaryMetricName}>Total Out (Debits)</span>
                    </div>
                    <div className={styles.summaryCardBody}>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 1</div>
                            <div className={`${styles.summaryAmount} ${styles.debitCell}`}>{formatCurrency(period1Debits)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period1Debits / period1Months)}</div>
                        </div>
                        <div className={styles.summaryPeriodSection}>
                            <div className={styles.summaryPeriodHeader}>Period 2</div>
                            <div className={`${styles.summaryAmount} ${styles.debitCell}`}>{formatCurrency(period2Debits)}</div>
                            <div className={styles.summaryAverage}>Avg/Month: {formatCurrency(period2Debits / period2Months)}</div>
                        </div>
                        <div className={styles.summaryChangeSection}>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>Difference:</span>
                                <span className={`${styles.summaryChangeValue} ${debitsDiff <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                                    {getTrendArrow(debitsDiff)} {formatCurrency(debitsDiff)}
                                </span>
                            </div>
                            <div className={styles.summaryChangeRow}>
                                <span className={styles.summaryChangeLabel}>% Change:</span>
                                <span className={`${styles.summaryChangeValue} ${debitsPercent <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                                    {formatPercent(debitsPercent)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Transaction Type Breakdown Component
 * Shows transaction types with counts and amounts, with collapsible category details
 */
function TransactionTypeBreakdown({ transactionTypes, categoryByType }) {
    const [expandedTypes, setExpandedTypes] = useState(new Set());

    const formatCurrency = (value) => formatMoney(Math.abs(value || 0));
    const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

    const getTrendArrow = (diff) => {
        if (diff > 0) return '↑';
        if (diff < 0) return '↓';
        return '→';
    };

    const toggleType = (type) => {
        const newExpanded = new Set(expandedTypes);
        if (newExpanded.has(type)) {
            newExpanded.delete(type);
        } else {
            newExpanded.add(type);
        }
        setExpandedTypes(newExpanded);
    };

    if (!transactionTypes || transactionTypes.length === 0) {
        return <div className={styles.noResultsMessage}>No transaction type data available</div>;
    }

    return (
        <>
            {/* Desktop Table View */}
            <div className={styles.transactionTypeTable}>
                {/* Header */}
                <div className={styles.typeTableHeader}>
                    <span className={styles.typeHeaderCell}>Type</span>
                    <span className={styles.typeHeaderCell}>Period 1 Count</span>
                    <span className={styles.typeHeaderCell}>Period 2 Count</span>
                    <span className={styles.typeHeaderCell}>Period 1 Total</span>
                    <span className={styles.typeHeaderCell}>Period 2 Total</span>
                    <span className={styles.typeHeaderCell}>Difference</span>
                    <span className={styles.typeHeaderCell}>% Change</span>
                </div>

                {/* Transaction Type Rows */}
                {transactionTypes.map((typeData) => {
                const { type, period1Count, period2Count, period1Total, period2Total } = typeData;
                const diff = period2Total - period1Total;
                const percent = period1Total !== 0 ? ((diff / Math.abs(period1Total)) * 100) : 0;
                const isExpanded = expandedTypes.has(type);

                // Get categories for this type
                const typeCategories = categoryByType.filter(cat => cat.type === type);

                return (
                    <div key={type}>
                        {/* Type Summary Row */}
                        <div
                            className={styles.typeRow}
                            onClick={() => toggleType(type)}
                            style={{ cursor: typeCategories.length > 0 ? 'pointer' : 'default' }}
                        >
                            <span className={styles.typeCell}>
                                {typeCategories.length > 0 && (
                                    <span className={styles.chevron}>{isExpanded ? '▼' : '▶'}</span>
                                )}
                                {type || 'Expense'}
                            </span>
                            <span className={styles.typeCell}>{period1Count || 0}</span>
                            <span className={styles.typeCell}>{period2Count || 0}</span>
                            <span className={styles.typeCell}>{formatCurrency(period1Total)}</span>
                            <span className={styles.typeCell}>{formatCurrency(period2Total)}</span>
                            <span className={`${styles.typeCell} ${diff >= 0 ? styles.positive : styles.negative}`}>
                                {getTrendArrow(diff)} {formatCurrency(diff)}
                            </span>
                            <span className={`${styles.typeCell} ${percent >= 0 ? styles.positive : styles.negative}`}>
                                {formatPercent(percent)}
                            </span>
                        </div>

                        {/* Expanded Category Details */}
                        {isExpanded && typeCategories.length > 0 && (
                            <div className={styles.categoryDetailsContainer}>
                                <div className={styles.categoryDetailsHeader}>
                                    <span className={styles.catDetailHeaderCell}>Category</span>
                                    <span className={styles.catDetailHeaderCell}>P1 Count</span>
                                    <span className={styles.catDetailHeaderCell}>P2 Count</span>
                                    <span className={styles.catDetailHeaderCell}>P1 Total</span>
                                    <span className={styles.catDetailHeaderCell}>P2 Total</span>
                                    <span className={styles.catDetailHeaderCell}>Diff</span>
                                    <span className={styles.catDetailHeaderCell}>% Chg</span>
                                </div>
                                {typeCategories.map((catData) => {
                                    const catDiff = catData.period2Total - catData.period1Total;
                                    const catPercent = catData.period1Total !== 0
                                        ? ((catDiff / Math.abs(catData.period1Total)) * 100)
                                        : 0;
                                    const isCeased = catData.endDate && new Date(catData.endDate) < new Date();

                                    return (
                                        <div key={catData.categoryKey} className={styles.categoryDetailRow}>
                                            <span className={`${styles.catDetailCell} ${isCeased ? styles.ceasedCategory : ''}`}>
                                                {catData.categoryName}
                                            </span>
                                            <span className={styles.catDetailCell}>{catData.period1Count || 0}</span>
                                            <span className={styles.catDetailCell}>{catData.period2Count || 0}</span>
                                            <span className={styles.catDetailCell}>{formatCurrency(catData.period1Total)}</span>
                                            <span className={styles.catDetailCell}>{formatCurrency(catData.period2Total)}</span>
                                            <span className={`${styles.catDetailCell} ${catDiff >= 0 ? styles.positive : styles.negative}`}>
                                                {getTrendArrow(catDiff)} {formatCurrency(catDiff)}
                                            </span>
                                            <span className={`${styles.catDetailCell} ${catPercent >= 0 ? styles.positive : styles.negative}`}>
                                                {formatPercent(catPercent)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            </div>

            {/* Mobile Card View */}
            <div className={styles.transactionTypeMobile}>
                {transactionTypes.map((typeData) => {
                    const { type, period1Count, period2Count, period1Total, period2Total } = typeData;
                    const diff = period2Total - period1Total;
                    const percent = period1Total !== 0 ? ((diff / Math.abs(period1Total)) * 100) : 0;
                    const isExpanded = expandedTypes.has(type);

                    // Get categories for this type
                    const typeCategories = categoryByType.filter(cat => cat.type === type);

                    return (
                        <div key={type} className={styles.typeCard}>
                            {/* Card Header - Always Visible */}
                            <div
                                className={styles.typeCardHeader}
                                onClick={() => toggleType(type)}
                            >
                                <div className={styles.typeCardTitle}>
                                    <span className={styles.typeName}>{type || 'Expense'}</span>
                                    {typeCategories.length > 0 && (
                                        <span className={styles.expandIcon}>
                                            {isExpanded ? '▲' : '▼'}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.typeCardSummary}>
                                    <div className={styles.typeSummaryRow}>
                                        <span className={styles.summaryLabel}>Period 1:</span>
                                        <span className={styles.summaryValue}>{period1Count || 0} txns | {formatCurrency(period1Total)}</span>
                                    </div>
                                    <div className={styles.typeSummaryRow}>
                                        <span className={styles.summaryLabel}>Period 2:</span>
                                        <span className={`${styles.summaryValue} ${diff >= 0 ? styles.positive : styles.negative}`}>
                                            {period2Count || 0} txns | {formatCurrency(period2Total)} {getTrendArrow(diff)} {formatCurrency(diff)} ({formatPercent(percent)})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Card Details - Expandable Category Breakdown */}
                            {isExpanded && typeCategories.length > 0 && (
                                <div className={styles.typeCardDetails}>
                                    <h4 className={styles.typeDetailTitle}>Categories</h4>
                                    {typeCategories.map((catData) => {
                                        const catDiff = catData.period2Total - catData.period1Total;
                                        const catPercent = catData.period1Total !== 0
                                            ? ((catDiff / Math.abs(catData.period1Total)) * 100)
                                            : 0;
                                        const isCeased = catData.endDate && new Date(catData.endDate) < new Date();

                                        return (
                                            <div key={catData.categoryKey} className={styles.typeDetailCategory}>
                                                <div className={`${styles.categoryDetailName} ${isCeased ? styles.ceasedCategory : ''}`}>
                                                    {catData.categoryName}
                                                </div>
                                                <div className={styles.categoryDetailRow}>
                                                    <span className={styles.detailLabel}>Period 1:</span>
                                                    <span className={styles.detailValue}>{catData.period1Count || 0} txns | {formatCurrency(catData.period1Total)}</span>
                                                </div>
                                                <div className={styles.categoryDetailRow}>
                                                    <span className={styles.detailLabel}>Period 2:</span>
                                                    <span className={`${styles.detailValue} ${catDiff >= 0 ? styles.positive : styles.negative}`}>
                                                        {catData.period2Count || 0} txns | {formatCurrency(catData.period2Total)} {getTrendArrow(catDiff)} {formatCurrency(catDiff)} ({formatPercent(catPercent)})
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

/**
 * Category Comparison Component
 * Shows all categories with their balances, credits, and debits for both periods
 * Desktop: Table view with all data visible
 * Mobile: Card view with expand/collapse for details
 */
function CategoryComparison({ categories }) {
    const [sortConfig, setSortConfig] = useState({ key: 'categoryName', direction: 'asc' });
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    const formatCurrency = (value) => formatMoney(Math.abs(value || 0));
    const formatPercent = (value) => {
        if (!isFinite(value)) return 'N/A';
        return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
    };

    const getTrendArrow = (diff) => {
        if (diff > 0) return '↑';
        if (diff < 0) return '↓';
        return '→';
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const toggleCategory = (categoryKey) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryKey)) {
            newExpanded.delete(categoryKey);
        } else {
            newExpanded.add(categoryKey);
        }
        setExpandedCategories(newExpanded);
    };

    if (!categories || categories.length === 0) {
        return <div className={styles.noResultsMessage}>No category data available</div>;
    }

    // Sort categories
    const sortedCategories = [...categories].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle string sorting (category name)
        if (sortConfig.key === 'categoryName') {
            aValue = (aValue || '').toLowerCase();
            bValue = (bValue || '').toLowerCase();
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        // Handle numeric sorting
        aValue = aValue || 0;
        bValue = bValue || 0;
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return (
        <>
            {/* Desktop Table View */}
            <div className={styles.categoryComparisonTable}>
                {/* Header */}
                <div className={styles.catCompTableHeader}>
                    <span className={styles.catCompHeaderCell} onClick={() => handleSort('categoryName')} style={{ cursor: 'pointer' }}>
                        Category {sortConfig.key === 'categoryName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </span>
                    <span className={styles.catCompHeaderCell}>P1 Balance</span>
                    <span className={styles.catCompHeaderCell}>P2 Balance</span>
                    <span className={styles.catCompHeaderCell}>Diff</span>
                    <span className={styles.catCompHeaderCell}>% Chg</span>
                    <span className={styles.catCompHeaderCell}>P1 In</span>
                    <span className={styles.catCompHeaderCell}>P2 In</span>
                    <span className={styles.catCompHeaderCell}>In Diff</span>
                    <span className={styles.catCompHeaderCell}>P1 Out</span>
                    <span className={styles.catCompHeaderCell}>P2 Out</span>
                    <span className={styles.catCompHeaderCell}>Out Diff</span>
                </div>

                {/* Category Rows */}
                {sortedCategories.map((cat) => {
                    const balanceDiff = (cat.period2Balance || 0) - (cat.period1Balance || 0);
                    const balancePercent = cat.period1Balance !== 0
                        ? ((balanceDiff / Math.abs(cat.period1Balance)) * 100)
                        : 0;

                    const creditsInDiff = (cat.period2CreditsIn || 0) - (cat.period1CreditsIn || 0);
                    const debitsOutDiff = (cat.period2DebitsOut || 0) - (cat.period1DebitsOut || 0);

                    const isCeased = cat.endDate && new Date(cat.endDate) < new Date();

                    return (
                        <div key={cat.categoryKey} className={styles.catCompRow}>
                            <span className={`${styles.catCompCell} ${isCeased ? styles.ceasedCategory : ''}`}>
                                {cat.categoryName}
                            </span>
                            <span className={styles.catCompCell}>{formatCurrency(cat.period1Balance)}</span>
                            <span className={styles.catCompCell}>{formatCurrency(cat.period2Balance)}</span>
                            <span className={`${styles.catCompCell} ${balanceDiff >= 0 ? styles.positive : styles.negative}`}>
                                {getTrendArrow(balanceDiff)} {formatCurrency(balanceDiff)}
                            </span>
                            <span className={`${styles.catCompCell} ${balancePercent >= 0 ? styles.positive : styles.negative}`}>
                                {formatPercent(balancePercent)}
                            </span>
                            <span className={`${styles.catCompCell} ${styles.creditCell}`}>{formatCurrency(cat.period1CreditsIn)}</span>
                            <span className={`${styles.catCompCell} ${styles.creditCell}`}>{formatCurrency(cat.period2CreditsIn)}</span>
                            <span className={`${styles.catCompCell} ${creditsInDiff >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                                {getTrendArrow(creditsInDiff)} {formatCurrency(creditsInDiff)}
                            </span>
                            <span className={`${styles.catCompCell} ${styles.debitCell}`}>{formatCurrency(cat.period1DebitsOut)}</span>
                            <span className={`${styles.catCompCell} ${styles.debitCell}`}>{formatCurrency(cat.period2DebitsOut)}</span>
                            <span className={`${styles.catCompCell} ${debitsOutDiff <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                                {getTrendArrow(debitsOutDiff)} {formatCurrency(debitsOutDiff)}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Mobile Card View */}
            <div className={styles.categoryComparisonMobile}>
                {sortedCategories.map((cat) => {
                    const balanceDiff = (cat.period2Balance || 0) - (cat.period1Balance || 0);
                    const balancePercent = cat.period1Balance !== 0
                        ? ((balanceDiff / Math.abs(cat.period1Balance)) * 100)
                        : 0;

                    const creditsInDiff = (cat.period2CreditsIn || 0) - (cat.period1CreditsIn || 0);
                    const debitsOutDiff = (cat.period2DebitsOut || 0) - (cat.period1DebitsOut || 0);

                    const isCeased = cat.endDate && new Date(cat.endDate) < new Date();
                    const isExpanded = expandedCategories.has(cat.categoryKey);

                    return (
                        <div key={cat.categoryKey} className={styles.categoryCard}>
                            {/* Card Header - Always Visible */}
                            <div
                                className={styles.categoryCardHeader}
                                onClick={() => toggleCategory(cat.categoryKey)}
                            >
                                <div className={styles.categoryCardTitle}>
                                    <span className={`${styles.categoryName} ${isCeased ? styles.ceasedCategory : ''}`}>
                                        {cat.categoryName}
                                    </span>
                                    <span className={styles.expandIcon}>
                                        {isExpanded ? '▲' : '▼'}
                                    </span>
                                </div>
                                <div className={styles.categoryCardSummary}>
                                    <div className={styles.periodBalance}>
                                        <span className={styles.periodLabel}>Period 1:</span>
                                        <span className={styles.periodValue}>{formatCurrency(cat.period1Balance)}</span>
                                    </div>
                                    <div className={styles.periodBalance}>
                                        <span className={styles.periodLabel}>Period 2:</span>
                                        <span className={`${styles.periodValue} ${balanceDiff >= 0 ? styles.positive : styles.negative}`}>
                                            {formatCurrency(cat.period2Balance)} {getTrendArrow(balanceDiff)} {formatCurrency(balanceDiff)} ({formatPercent(balancePercent)})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Card Details - Expandable */}
                            {isExpanded && (
                                <div className={styles.categoryCardDetails}>
                                    <div className={styles.detailSection}>
                                        <h4 className={styles.detailSectionTitle}>Period 1</h4>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Balance:</span>
                                            <span className={styles.detailValue}>{formatCurrency(cat.period1Balance)}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Credits In:</span>
                                            <span className={`${styles.detailValue} ${styles.creditCell}`}>{formatCurrency(cat.period1CreditsIn)}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Debits Out:</span>
                                            <span className={`${styles.detailValue} ${styles.debitCell}`}>{formatCurrency(cat.period1DebitsOut)}</span>
                                        </div>
                                    </div>

                                    <div className={styles.detailSection}>
                                        <h4 className={styles.detailSectionTitle}>Period 2</h4>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Balance:</span>
                                            <span className={`${styles.detailValue} ${balanceDiff >= 0 ? styles.positive : styles.negative}`}>
                                                {formatCurrency(cat.period2Balance)} {getTrendArrow(balanceDiff)} {formatCurrency(balanceDiff)} ({formatPercent(balancePercent)})
                                            </span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Credits In:</span>
                                            <span className={`${styles.detailValue} ${creditsInDiff >= 0 ? styles.positiveCredit : styles.negativeCredit}`}>
                                                {formatCurrency(cat.period2CreditsIn)} {getTrendArrow(creditsInDiff)} {formatCurrency(creditsInDiff)}
                                            </span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Debits Out:</span>
                                            <span className={`${styles.detailValue} ${debitsOutDiff <= 0 ? styles.positiveDebit : styles.negativeDebit}`}>
                                                {formatCurrency(cat.period2DebitsOut)} {getTrendArrow(debitsOutDiff)} {formatCurrency(debitsOutDiff)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

export default MonthComparisonReport;
